import { z } from "zod";

import { optionalQueryParam, PaginationParamsSchema } from "./common.schemas";

// ============================================================================
// TICKET ENUMS
// ============================================================================

export const TicketTypeSchema = z.enum(["BUG", "FEATURE_REQUEST", "IMPROVEMENT"]);

export const TicketPrioritySchema = z.enum(["NONE", "URGENT", "HIGH", "MEDIUM", "LOW"]);

/**
 * Ticket `type` is stored in Linear as a label, so these three label names are
 * reserved: they are written from `type` and read back into `type`. Callers set
 * them via `type`, never via `labels` — see `TicketLabelsSchema`.
 *
 * 🔴 EACH VALUE MUST BE THE WORKSPACE'S REAL LABEL DISPLAY NAME, CASE ASIDE.
 * A value differing by anything more than case matches no existing label, and
 * the write path then MINTS a second one under this name instead of attaching
 * the real one — silently, and once per filing. `"feature-request"` against a
 * workspace spelling it `Feature request` is what put 43 tickets on a duplicate
 * (NEX-5019).
 */
export const TICKET_TYPE_LABELS: Record<z.infer<typeof TicketTypeSchema>, string> = {
  BUG: "Bug",
  FEATURE_REQUEST: "Feature request",
  IMPROVEMENT: "Improvement"
};

/**
 * `TICKET_TYPE_LABELS` reversed and keyed on the lowercased label name — one
 * case-folded lookup every reader shares, so how a label name is matched is
 * decided here and not once per consumer. Reading a `type` off a Linear label,
 * and rejecting a reserved name in `labels`, are the same question asked twice.
 */
export const TICKET_TYPE_BY_LOWERCASE_LABEL: Readonly<
  Record<string, z.infer<typeof TicketTypeSchema>>
> = Object.freeze(
  // `Object.fromEntries` is typed `{ [k: string]: T }` and cannot narrow its
  // value type from the tuple it was handed, so the annotation is the only place
  // that type can be stated. The cast claims nothing about runtime data — every
  // value comes from `TICKET_TYPE_LABELS`'s own keys, which ARE the enum.
  Object.fromEntries(
    Object.entries(TICKET_TYPE_LABELS).map(([type, label]) => [label.toLowerCase(), type])
  ) as Record<string, z.infer<typeof TicketTypeSchema>>
);

/**
 * Spellings that USED to carry a ticket type and no longer match any Linear
 * label, kept refused in `labels` rather than dropped.
 *
 * 🔴 THIS LIST IS A SUNSET, AND ONLY THIS LIST IS. A name here matches no label
 * in the workspace, so `resolveLabelIds` in the adapter would refuse it anyway;
 * what the entry still buys is WHICH refusal the caller gets — "that is a
 * reserved ticket-type label, use the `type` field instead" rather than the
 * generic "does not match any label". A caller still sending the old spelling
 * is told what to do instead of what is wrong.
 *
 * A name leaves this list once no caller could still be sending it, and then
 * the generic refusal is the whole answer.
 *
 * 🔴 THE SET BELOW IS NOT A SUNSET AND MUST NOT BE READ AS ONE. The LIVE type
 * names — the keys of `TICKET_TYPE_BY_LOWERCASE_LABEL` — DO exist as labels in
 * the workspace, so the adapter's refusal would resolve them happily and attach
 * one, leaving `type` and `labels` disagreeing about the same ticket. That
 * check is load-bearing against the general mechanism rather than duplicated by
 * it, and deleting it re-opens the disagreement the two fields exist to
 * prevent.
 */
const RETIRED_TYPE_LABEL_NAMES = ["feature-request"] as const;

const RESERVED_TYPE_LABEL_NAMES = new Set([
  ...Object.keys(TICKET_TYPE_BY_LOWERCASE_LABEL),
  ...RETIRED_TYPE_LABEL_NAMES
]);

/** Longest Linear label name accepted on a ticket write. */
export const TICKET_LABEL_MAX_LENGTH = 64;

/** Most labels a single ticket write may carry, to bound the label-resolution work a write does. */
export const TICKET_LABELS_MAX_COUNT = 20;

/**
 * Canonical Linear workflow-state names a ticket can transition to. These mirror
 * the values accepted by `ticket list --status`. The status is resolved against
 * the actual states configured on the Linear team in the backend adapter — on
 * BOTH the write path and the list filter — so custom team states are accepted
 * and anything else is rejected with the complete allowed set (NEX-3087).
 *
 * This list documents the common values for the OpenAPI surface. It is not a
 * hard enum here on purpose: the real allowed set is per-team and only knowable
 * at runtime, so freezing it in the schema would reject legitimate custom states.
 */
export const TICKET_STATUS_NAMES = [
  "Triage",
  "Backlog",
  "Todo",
  "In Progress",
  "In Review",
  "Done",
  "Canceled"
] as const;

// ============================================================================
// TICKET CONTEXT
// ============================================================================

export const TicketContextSchema = z.object({
  endpoint: z.string().optional(),
  method: z.string().optional(),
  statusCode: z.number().int().optional(),
  errorCode: z.string().optional(),
  requestBody: z.string().max(2000).optional(),
  responseBody: z.string().max(2000).optional(),
  reproductionSteps: z.string().optional(),
  expectedBehavior: z.string().optional(),
  actualBehavior: z.string().optional(),
  environment: z.string().optional(),
  sdkVersion: z.string().optional(),
  agentId: z.string().uuid().optional()
});

// ============================================================================
// TICKET LABELS
// ============================================================================

/**
 * Linear labels to attach to a ticket (e.g. "CUE" to mark an agent-filed
 * ticket). Names are matched case-insensitively against the labels this team
 * or workspace already carries; a name matching none is refused with the
 * allowed set rather than minted, so the vocabulary is the workspace's own and
 * a write cannot widen it.
 *
 * The three type labels ("Bug", "Feature request", "Improvement") are reserved
 * for `type` and rejected here, so the two fields can never disagree.
 *
 * The `.describe()` is not decoration. This schema is compiled to the JSON
 * Schema the MCP ticket tools advertise, and the model filling `labels` reads
 * that and nothing else — a JSDoc comment does not survive the compilation.
 * Without it the constraint is discoverable only by making a call that fails,
 * which costs a guaranteed-losing round trip per novel name.
 */
export const TicketLabelsSchema = z
  .array(z.string().trim().min(1).max(TICKET_LABEL_MAX_LENGTH))
  .max(TICKET_LABELS_MAX_COUNT)
  .describe(
    "Labels to attach. Each name must already exist on the team or workspace — " +
      "an unrecognised name is rejected with the list of allowed names, and is " +
      "never created. Matched case-insensitively. Do not pass a ticket-type " +
      'label ("Bug", "Feature request", "Improvement"); use the `type` field.'
  )
  .superRefine((labels, ctx) => {
    labels.forEach((label, index) => {
      if (RESERVED_TYPE_LABEL_NAMES.has(label.toLowerCase())) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: [index],
          message: `"${label}" is a reserved ticket-type label. Set the ticket type with the "type" field instead.`
        });
      }
    });
  });

// ============================================================================
// TICKET SUMMARY (for list endpoint)
// ============================================================================

export const TicketSummarySchema = z.object({
  id: z.string(),
  identifier: z.string(),
  title: z.string(),
  type: TicketTypeSchema.nullable(),
  priority: TicketPrioritySchema,
  status: z.string(),
  url: z.string(),
  /**
   * Linear labels on the ticket, excluding the reserved type label — that one is
   * surfaced as `type`. Safe to feed straight back into a create/update `labels`.
   */
  labels: z.array(z.string()),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date().nullable()
});

// ============================================================================
// TICKET DETAIL (for get endpoint - includes more fields)
// ============================================================================

export const TicketDetailSchema = TicketSummarySchema.extend({
  description: z.string().nullable(),
  context: TicketContextSchema.nullable(),
  /**
   * When the ticket was archived, or `null` while it is live.
   *
   * An archived ticket is READ-ONLY: comments and attachments are refused with
   * 409, because the provider refuses those two writes outright and answers
   * "Could not find referenced Issue" — a sentence that contradicts the 200 this
   * very read just returned (NEX-3469).
   *
   * It appears on the detail schema only, and that is a fact about the data
   * rather than an omission: the list endpoint resolves tickets through customer
   * needs, archiving an issue archives its need too, so an archived ticket
   * cannot appear in a listing. A summary field would be permanently null.
   */
  archivedAt: z.coerce.date().nullable()
});

// ============================================================================
// PARAMS
// ============================================================================

export const TicketIdParamSchema = z.object({
  ticketId: z.string()
});

// ============================================================================
// LIST TICKETS
// ============================================================================

export const ListTicketsParamsSchema = PaginationParamsSchema.extend({
  // `optionalQueryParam`, not a bare `.optional()`. Both are enum-valued list
  // filters parsed from the query string, so `?type=` — the cleared dropdown,
  // which `qs` delivers as `""` — has to mean "no filter" rather than an
  // out-of-enum value. A bare `.optional()` answered it with
  // `Invalid option: expected one of "BUG"|…`, naming a set that cannot contain
  // `""`, which is the exact rejection-of-something-that-was-never-wrong this
  // issue is about; `/documents?type=` and `/tools/search?type=` already answer
  // 200, and "these filters answer three different ways" is the defect, so
  // leaving these two behind would just move the inconsistency (NEX-3087).
  // `list()` reads both with `if (options.type)`, so absence is already its
  // "no filter", and an in-enum value is unaffected.
  type: optionalQueryParam(TicketTypeSchema),
  priority: optionalQueryParam(TicketPrioritySchema),
  /**
   * One workflow-state name, or several comma-separated (`"Todo,In Progress"`).
   * Matched case-insensitively against the team's configured states; an unknown
   * name is rejected with the complete allowed set rather than silently matching
   * nothing. Stays `z.string()` here because the allowed set is per-team and
   * only resolvable at runtime — see `TICKET_STATUS_NAMES`.
   */
  status: z.string().optional(),
  search: z.string().optional()
});

/**
 * The single-org ticket list's `{tickets, meta}` shape.
 *
 * 🔴 `total` AND `totalPages` ARE OPTIONAL, AND THEY TRAVEL TOGETHER. The
 * upstream provider fetch is bounded, so past that bound the server cannot
 * establish a count — and it publishes none rather than a floor wearing a total's
 * name. Present means exact; absent means unknown. `totalPages` is derived from
 * `total` and inherits its uncertainty exactly, so it is absent whenever `total`
 * is.
 *
 * ⚠️ `hasMore` IS THE FIELD TO PAGE ON, and it is always present. It reports
 * REACHABILITY — whether a further page will return rows — so it terminates even
 * when rows exist beyond the bound. That second fact is what the absent `total`
 * carries.
 */
export const ListTicketsResponseSchema = z.object({
  tickets: z.array(TicketSummarySchema),
  meta: z.object({
    total: z.number().int().optional(),
    totalPages: z.number().int().optional(),
    page: z.number().int(),
    limit: z.number().int(),
    hasMore: z.boolean()
  })
});

// ============================================================================
// GET TICKET
// ============================================================================

export const GetTicketResponseSchema = TicketDetailSchema;

// ============================================================================
// CREATE TICKET
// ============================================================================

export const CreateTicketBodySchema = z.object({
  title: z.string().trim().min(1),
  description: z.string().optional(),
  type: TicketTypeSchema.optional().default("BUG"),
  priority: TicketPrioritySchema.optional().default("MEDIUM"),
  context: TicketContextSchema.optional(),
  /** Extra Linear labels to attach on creation, e.g. `["CUE"]`. */
  labels: TicketLabelsSchema.optional()
});

export const CreateTicketResponseSchema = TicketDetailSchema;

// ============================================================================
// UPDATE TICKET
// ============================================================================

export const UpdateTicketBodySchema = z.object({
  title: z.string().trim().min(1).optional(),
  description: z.string().optional(),
  type: TicketTypeSchema.optional(),
  priority: TicketPrioritySchema.optional(),
  /**
   * Workflow-state name to transition the ticket to (e.g. "Canceled", "Done",
   * "In Progress"). Resolved to a Linear workflow-state id and validated against
   * the team's configured states in the adapter.
   */
  status: z.string().min(1).optional(),
  /**
   * Replaces the ticket's non-type labels wholesale. Omit to leave them alone;
   * pass `[]` to clear them. The type label is preserved regardless.
   */
  labels: TicketLabelsSchema.optional()
});

export const UpdateTicketResponseSchema = TicketDetailSchema;

// ============================================================================
// TICKET COMMENTS
// ============================================================================

export const TicketCommentSchema = z.object({
  id: z.string(),
  body: z.string(),
  authorName: z.string().nullable(),
  createdAt: z.coerce.date()
});

export const CreateTicketCommentBodySchema = z.object({
  body: z.string().min(1)
});

export const ListTicketCommentsResponseSchema = z.object({
  comments: z.array(TicketCommentSchema)
});

// ============================================================================
// TICKET ATTACHMENTS
// ============================================================================

export const TicketAttachmentSchema = z.object({
  id: z.string(),
  filename: z.string(),
  url: z.string(),
  contentType: z.string().nullable(),
  size: z.number().nullable(),
  createdAt: z.coerce.date()
});

export const UploadTicketAttachmentResponseSchema = TicketAttachmentSchema;

export const ListTicketAttachmentsResponseSchema = z.object({
  attachments: z.array(TicketAttachmentSchema)
});
